/**
 * Archivos clínicos en el servidor (PostgreSQL + disco/GCS).
 */
import {
  uploadClinicalFile,
  getClinicalFileMeta,
  deleteClinicalFileApi,
  clinicalFileAbsoluteUrl,
} from '../api/consultorioClinical';

const LS_THRESHOLD = 280_000;

export function shouldStoreExternally(dataUrlOrSize) {
  if (typeof dataUrlOrSize === 'number') return dataUrlOrSize > LS_THRESHOLD;
  const s = String(dataUrlOrSize || '');
  return s.length > LS_THRESHOLD;
}

export async function storeClinicalFile(dataUrl, meta = {}) {
  const id =
    meta.storageId ||
    (meta.radiografiaId ? `cf-${String(meta.radiografiaId).replace(/^cf-/, '')}` : '') ||
    `cf-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  const url = String(dataUrl || '');
  const byteSize = meta.byteSize > 0 ? meta.byteSize : Math.ceil((url.length * 3) / 4);
  const res = await uploadClinicalFile({
    externalId: id,
    dataUrl: url,
    mime: meta.mime,
    filename: meta.nombre,
    meta: {
      tenantId: meta.tenantId,
      idPaciente: meta.idPaciente,
      radiografiaId: meta.radiografiaId,
      codigoArchivo: meta.codigoArchivo,
      category: meta.category || 'radiografia',
      byteSize,
    },
  });
  return res.external_id;
}

export async function getClinicalFile(id) {
  if (!id) return null;
  try {
    const meta = await getClinicalFileMeta(id);
    return {
      id,
      dataUrl: clinicalFileAbsoluteUrl(meta.url),
      mime: meta.mime || '',
      nombre: meta.meta?.nombre || '',
      byteSize: meta.byte_size,
    };
  } catch {
    return null;
  }
}

export async function deleteClinicalFile(id) {
  if (!id) return;
  try {
    await deleteClinicalFileApi(id);
  } catch {
    /* noop */
  }
}

export async function resolveClinicalAttachment(record = {}) {
  const inline = record.archivoDataUrl || record.dataUrl || '';
  if (inline && String(inline).length > 64 && !record.archivoStorageId) {
    return {
      dataUrl: inline,
      mime: record.archivoMime || record.mime || '',
      nombre: record.archivoNombre || record.nombre || '',
    };
  }
  const storageId = record.archivoStorageId || record.storageId || '';
  if (!storageId) {
    return { dataUrl: inline || '', mime: record.archivoMime || '', nombre: record.archivoNombre || '' };
  }
  try {
    const meta = await getClinicalFileMeta(storageId);
    const url = clinicalFileAbsoluteUrl(meta.url);
    return {
      dataUrl: url,
      mime: meta.mime || record.archivoMime || '',
      nombre: record.archivoNombre || record.nombre || '',
      radiografiaId: record.id || '',
      codigoArchivo: record.codigoArchivo || '',
    };
  } catch {
    return { dataUrl: '', mime: '', nombre: '' };
  }
}

export function isPdfDataUrl(dataUrl) {
  const s = String(dataUrl || '');
  return s.startsWith('data:application/pdf') || s.includes('application/pdf') || /\.pdf(\?|$)/i.test(s);
}

export function isImageDataUrl(dataUrl) {
  const s = String(dataUrl || '');
  return s.startsWith('data:image') || /\.(jpe?g|png|gif|webp)(\?|$)/i.test(s);
}

export async function prepareAttachmentForSave(dataUrl, meta = {}) {
  const url = String(dataUrl || '');
  const byteSize = meta.byteSize > 0 ? meta.byteSize : Math.ceil((url.length * 3) / 4);
  if (!url) {
    return { dataUrl: '', storageId: '', mime: meta.mime || '', nombre: meta.nombre || '', byteSize: 0 };
  }
  const storageId =
    meta.storageId ||
    (meta.radiografiaId ? `cf-${String(meta.radiografiaId).replace(/^cf-/, '')}` : '') ||
    `cf-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  const uploaded = await uploadClinicalFile({
    externalId: storageId,
    dataUrl: url,
    mime: meta.mime,
    filename: meta.nombre,
    meta,
  });
  return {
    dataUrl: '',
    storageId: uploaded.external_id,
    fileUrl: clinicalFileAbsoluteUrl(uploaded.url),
    mime: meta.mime || uploaded.mime || '',
    nombre: meta.nombre || '',
    byteSize: uploaded.byte_size || byteSize,
  };
}
